import React from 'react';
import catalog from '../../../public/seo-routes.json';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const DEFAULT_SITE_URL =
  (import.meta as any).env?.VITE_SITE_URL || 'https://sor-seo.com';
const BASE_SITE_URL = DEFAULT_SITE_URL.replace(/\/+$/, '');
const DEFAULT_OG_IMAGE = `${BASE_SITE_URL}/logoSORBlack.png`;

type SeoProps = {
  title: string;
  description: string;
  canonicalPath?: string;
  robots?: string;
  ogImage?: string;
};

const buildCanonicalUrl = (pathname: string, canonicalPath?: string) => {
  const rawPath = canonicalPath ?? pathname ?? '/';
  const normalizedPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  if (normalizedPath === '/') {
    return `${BASE_SITE_URL}/`;
  }
  return `${BASE_SITE_URL}${normalizedPath}`;
};

const ensureAbsoluteUrl = (value?: string) => {
  if (!value) return undefined;
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  if (value.startsWith('//')) {
    return `https:${value}`;
  }
  const normalizedPath = value.startsWith('/') ? value : `/${value}`;
  return `${BASE_SITE_URL}${normalizedPath}`;
};

const clampText = (value: string, maxLength: number) => {
  const normalized = value.trim().replace(/\s+/g, ' ');
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 3).trimEnd()}...`;
};

export const Seo: React.FC<SeoProps> = ({
  title,
  description,
  canonicalPath,
  robots = 'index,follow',
  ogImage,
}) => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const route = (catalog.routes as Record<string, any>)[normalizedPath];
  const htmlLang =
    route?.lang || (i18n.language?.startsWith('fr') ? 'fr' : 'en');
  const canonicalUrl = buildCanonicalUrl(
    pathname,
    route?.canonicalPath || canonicalPath,
  );
  const metaTitle = route?.title || title;
  const metaDescription = route?.description || clampText(description, 160);
  const resolvedOgImage =
    ensureAbsoluteUrl(route?.ogImage || ogImage) || DEFAULT_OG_IMAGE;
  const resolvedRobots = route?.robots || 'noindex,follow';
  const alternates = route?.alternates as Record<string, string> | undefined;

  return (
    <Helmet htmlAttributes={{ lang: htmlLang }}>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="robots" content={resolvedRobots} />
      <link rel="canonical" href={canonicalUrl} />

      {alternates &&
        Object.entries({ ...alternates, 'x-default': alternates.en }).map(
          ([lang, path]) => (
            <link
              key={lang}
              rel="alternate"
              hrefLang={lang}
              href={buildCanonicalUrl(path)}
            />
          ),
        )}
      <meta
        property="og:locale"
        content={htmlLang === 'fr' ? 'fr_CA' : 'en_CA'}
      />
      <meta property="og:image:alt" content={metaTitle} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SOR SEO" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />
    </Helmet>
  );
};

export default Seo;
